"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.refinePlan = refinePlan;
const path = __importStar(require("path"));
const dist_1 = require("../../core/dist");
const aiSummarization_1 = require("./utils/aiSummarization");
const createPlan_1 = require("./createPlan");
const SYSTEM_PROMPT = `You are an AI assistant embedded in REslava Loom, a document-driven workflow system.
Your task: read this plan document and produce an improved version — clarify step descriptions, add missing steps, fix blocker references, improve the notes section.
Preserve steps already marked done (✅). Do not change their done status.
If the Additional Context contains a locked requirements (req) doc — sections ✅ Included / ❌ Excluded / ⛓ Constraints, each item prefixed with an inline-code \`IN\`/\`EX\`/\`C\` id — then:
- Treat every ❌ Excluded item and ⛓ Constraint as a HARD BOUNDARY: never add a step that pursues Excluded work.
- Make sure every ✅ Included item is advanced by at least one step.
- In each step's Satisfies cell, list the \`IN\`/\`C\` ids that step advances (comma-separated, or — if none). Never cite an \`EX\` id positively.
- Keep any Satisfies ids already present on a step unless you remove the step itself.
Respond with exactly this format — nothing else before or after:

TITLE: <improved or unchanged title>

## Goal
<what this plan implements in 1-2 sentences>

## Steps
| Done | # | Step | Files touched | Blocked by | Satisfies |
|------|---|------|---------------|------------|-----------|
| 🔳 | 1 | <step> | <files> | — | — |

## Notes
<implementation notes, one bullet per step if needed>`;
async function refinePlan(input, deps) {
    const doc = await deps.loadDoc(input.filePath);
    const content = input.extraContext
        ? `## Additional Context\n\n${input.extraContext}\n\n---\n\n${doc.content}`
        : doc.content;
    const messages = (0, aiSummarization_1.buildSummarizationMessages)(SYSTEM_PROMPT, `plan document titled "${doc.title}"`, content);
    const reply = await deps.aiClient.complete(messages);
    const { title, body } = (0, aiSummarization_1.parseTitleAndBody)(reply);
    // Re-parse the refined table so the AI's step edits and Satisfies citations
    // actually take effect (saveDoc regenerates the table from PlanDoc.steps).
    // Merge against the loaded steps so a refine never (a) changes a step's done
    // status or (b) silently strips a citation the table previously carried.
    const parsedSteps = (0, dist_1.parseStepsTable)(body);
    const oldByOrder = new Map((doc.steps ?? []).map(s => [s.order, s]));
    const mergedSteps = parsedSteps.length > 0
        ? parsedSteps.map(s => {
            const prev = oldByOrder.get(s.order);
            return {
                ...s,
                status: prev ? prev.status : s.status,
                satisfies: s.satisfies.length ? s.satisfies : (prev?.satisfies ?? []),
            };
        })
        : (doc.steps ?? []); // malformed/empty AI table → keep existing steps, never wipe
    // Re-baseline the staleness marker: a refine brings the plan up to the current
    // design, so stamp design_version = the live design version. Without this, refine
    // bumps the plan's own version but leaves the stale baseline behind, so the plan
    // stays flagged "stale" forever — the very operation meant to clear it never could.
    const threadDir = path.dirname(path.dirname(input.filePath));
    const threadSlug = path.basename(threadDir);
    const design = await (0, createPlan_1.parentDesignVersion)(threadDir, threadSlug, { loadDoc: deps.loadDoc, fs: deps.fs });
    const updated = {
        ...doc,
        title,
        version: doc.version + 1,
        updated: (0, dist_1.today)(),
        ...(design ? { design_version: design.version } : {}),
        steps: mergedSteps,
        content: `# ${title}\n\n${body}`,
    };
    await deps.saveDoc(updated, input.filePath);
    return { filePath: input.filePath, version: updated.version };
}
